<template>
  <fragment>
    <div class="col-12 col-lg-8">
      <div class="dashboard-card">
        <div class="row">
          <div class="col-12" style="margin-bottom: 20px">
            <h2>Personal Info</h2>
          </div>
        </div>

        <form-component>
          <form
            id="account"
            data-vv-scope="account"
            novalidate="novalidate"
            @submit.prevent="onSubmit"
          >
            <div class="form-row">
              <div class="col-12 col-md-6">
                <input-text
                  id="firstName"
                  v-model="firstName"
                  v-validate="'required'"
                  name="firstName"
                  data-vv-name="firstName"
                  data-vv-as="first name"
                  label="First Name"
                  type="text"
                  required
                  :error="errors.first('account.firstName')"
                />
              </div>
              <div class="col-12 col-md-6">
                <input-text
                  id="lastName"
                  v-model="lastName"
                  v-validate="'required'"
                  name="lastName"
                  data-vv-name="lastName"
                  data-vv-as="last name"
                  label="Last Name"
                  type="text"
                  required
                  :error="errors.first('account.lastName')"
                />
              </div>
            </div>
            <div class="form-row">
              <div class="col-12 col-md-6">
                <phone-number-input
                  id="phone"
                  v-model="phone"
                  v-validate="'required'"
                  label="Phone Number"
                  :error="errors.first('account.phone')"
                  name="phone"
                  data-vv-name="phone"
                  data-vv-as="phone number"
                />
              </div>
              <div class="col-12 col-md-6 recieve-sms-input">
                <b-form-checkbox
                  id="allowSmsNotifications"
                  v-model="allowSmsNotifications"
                  size="lg"
                  name="allowSmsNotifications"
                  type="checkbox"
                />
                <label class="replacement-label" for="allowSmsNotifications">
                  Receive text message reminders about upcoming sessions
                </label>
              </div>
              <div class="col-12 col-xl-6">
                <birthday-picker v-model="dateOfBirth" />
              </div>
              <div class="col-12 col-xl-6">
                <location-picker v-model="currentLocation" />
              </div>
            </div>
            <div class="form-row">
              <div class="col-12 col-xl-6">
                <gender-select
                  id="gender"
                  v-model="gender"
                  v-validate="'required'"
                  name="gender"
                  data-vv-name="gender"
                  data-vv-as="gender"
                  label="GENDER IDENTITY"
                  :select-options="studentGenderOptions"
                  :placeholder="null"
                  required
                  :error="errors.first('account.gender')"
                />
              </div>
              <div class="col-12 col-xl-6">
                <ethnicity-select
                  id="ethnicity"
                  v-model="ethnicity"
                  v-validate="'required'"
                  name="ethnicity"
                  data-vv-name="ethnicity"
                  data-vv-as="ethnicity"
                  label="ETHNICITY"
                  :prefer-not-to-answer="true"
                  required
                  :error="errors.first('account.ethnicity')"
                />
              </div>
            </div>
            <div class="form-row">
              <div class="col-12">
                <input-text-area
                  id="biography"
                  v-model="biography"
                  name="biography"
                  label="Biography"
                  type="text"
                />
              </div>
            </div>

            <hr />

            <h2>Counselor Preferences</h2>
            <p class="description">
              Whenever possible counselors will be suggested that match your
              preferences.
            </p>

            <div class="form-row">
              <div class="col-12 col-xl-6">
                <div class="form-row">
                  <div class="col-12">
                    <ethnicity-select
                      id="ethnicityPreference"
                      v-model="ethnicityPreference"
                      name="ethnicityPreference"
                      label="PREFERRED ETHNICITY"
                      empty-label="No Preference"
                    />
                  </div>
                </div>
              </div>

              <div class="col-12 col-xl-6">
                <div class="form-row">
                  <div class="col-12">
                    <input-select
                      id="genderPreference"
                      v-model="genderPreference"
                      name="genderPreference"
                      type="select"
                      label="PREFERRED GENDER"
                      :placeholder="null"
                      :options="counselorPreferenceGenderOptions"
                    />
                  </div>
                </div>
              </div>

              <div class="col-12 col-xl-6">
                <div class="form-row">
                  <div class="col-12">
                    <input-select
                      id="lgbtqPreference"
                      v-model="lgbtqPreference"
                      name="lgbtqPreference"
                      label="PREFERRED LGBTQ+ STATUS"
                      :placeholder="null"
                      :options="[
                        { id: 'true', display_label: 'Yes' },
                        { id: 'false', display_label: 'No Preference' }
                      ]"
                    />
                  </div>
                </div>
              </div>

              <div class="col-12 col-xl-6">
                <input-select
                  id="religionPreference"
                  v-model="religionPreference"
                  name="religionPreference"
                  label="PREFERRED RELIGION"
                  placeholder="No Preference"
                  placeholder-enabled
                  :options="[
                    { id: 'agnostic', display_label: 'Agnostic' },
                    { id: 'atheist', display_label: 'Atheist' },
                    { id: 'catholic', display_label: 'Catholic' },
                    { id: 'hindu', display_label: 'Hindu' },
                    { id: 'jewish', display_label: 'Jewish' },
                    { id: 'mormon', display_label: 'Mormon' },
                    { id: 'muslim', display_label: 'Muslim' },
                    { id: 'protestant', display_label: 'Protestant' },
                    { id: 'unaffiliated', display_label: 'Unaffiliated' },
                    { id: 'other', display_label: 'Other' }
                  ]"
                />
              </div>

              <div class="col-12 col-xl-6">
                <language-picker v-model="languagePreference" />
              </div>

              <div class="col-12">
                <hr />
                <h2>Struggle Areas</h2>

                <p class="description">
                  Only counselors that you schedule a session with will be
                  able to see your struggle areas.
                </p>

                <label class="replacement-label" style="margin-bottom: 5px;">STRUGGLE AREAS</label>
                <div class="form-row hide-label">
                  <div class="col-12">
                    <struggle-picker
                      v-model="struggleAreas"
                      data-tags="struggleAreas"
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />

            <button
              type="submit"
              :disabled="saving"
              class="btn btn-primary btn-wide"
            >
              <font-awesome-icon
                v-if="saving"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Save Changes</span>
            </button>
          </form>
        </form-component>
      </div>
    </div>
    <div class="col-12 col-lg-4">
      <div class="dashboard-card">
        <profile-photo-block>
          Upload a photo of yourself to personalize your BetterMynd experience.
        </profile-photo-block>
      </div>

      <div class="dashboard-card">
        <security-block />
      </div>
    </div>
  </fragment>
</template>

<script>
import StudentClient from '@/api/StudentClient';
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import EthnicitySelect from '@/components/inputs/EthnicitySelect.vue';
import GenderSelect from '../../components/inputs/GenderSelect.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputTextArea from '@/components/inputs/InputTextArea.vue';
import { tagFromName } from '@/helpers/tags';
import SecurityBlock from '@/users/account/components/SecurityBlock.vue';
import ProfilePhotoBlock from '@/users/account/components/ProfilePhotoBlock.vue';
import BirthdayPicker from './components/BirthdayPicker.vue';
import LanguagePicker from './components/LanguagePicker.vue';
import LocationPicker from './components/LocationPicker.vue';
import StrugglePicker from './components/StrugglePicker.vue';
import genderOptions from '@/helpers/mixins/gender-options';
import PhoneNumberInput from '@/components/inputs/PhoneNumberInput.vue';

export default {
  name: 'StudentAccount',
  components: {
    FormComponent,
    EthnicitySelect,
    GenderSelect,
    InputSelect,
    InputText,
    InputTextArea,
    SecurityBlock,
    ProfilePhotoBlock,
    BirthdayPicker,
    LanguagePicker,
    LocationPicker,
    StrugglePicker,
    PhoneNumberInput
  },
  mixins: [genderOptions],

  props: {
    user: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      userClient: new UserClient(this.$http),
      studentClient: new StudentClient(this.$http),

      saving: false,

      firstName: '',
      lastName: '',
      phone: '',
      allowSmsNotifications: false,
      dateOfBirth: {
        year: '',
        month: '',
        day: ''
      },
      currentLocation: null,
      gender: '',
      ethnicity: '',
      biography: '',

      ethnicityPreference: '',
      genderPreference: '',
      lgbtqPreference: 'false',
      religionPreference: '',
      languagePreference: '',
      struggleAreas: []
    };
  },

  async mounted() {
    this.firstName = this.user.first_name || '';
    this.lastName = this.user.last_name || '';
    this.phone = (this.user.phone_number || '').replace(/^\+1/, '');
    this.allowSmsNotifications = this.user.allow_sms_notifications;

    const dob = this.user.date_of_birth
      ? this.user.date_of_birth.split('-')
      : [];
    if (dob.length === 3) {
      this.dateOfBirth = {
        year: parseInt(dob[0], 10).toString(),
        month: parseInt(dob[1], 10).toString(),
        day: parseInt(dob[2], 10).toString()
      };
    }

    this.currentLocation = this.user.city;
    this.gender = (this.user.gender || '').toLowerCase();
    this.ethnicity = this.user.race || 'preferNotToAnswer';
    this.biography = this.user.bio || '';

    const student = this.user.student_profile;
    if (!student) {
      this.$store.commit('setGlobalAlert', {
        msg: 'There was an error loading this page. Please try again!',
        error: true
      });
      console.error('user.student_profile was falsy!', this.user);
      return;
    }

    const preferences = await this.studentClient.getCounselorPreferences();
    if (!preferences) {
      this.$store.commit('setGlobalAlert', {
        msg: 'There was an error loading this page. Please try again!',
        error: true
      });
      console.error('counselor_prefs was falsy!', this.user);
      return;
    }

    this.ethnicityPreference = preferences.race_preference || '';
    this.genderPreference = preferences.gender_preference || '';
    this.lgbtqPreference = (preferences.lgbtq_preference || false).toString();
    this.religionPreference = preferences.religion_preference || '';
    this.languagePreference = preferences.language_preference ? preferences.language_preference.toString() : '';
    this.struggleAreas = (preferences.struggle_areas || []).map(tagFromName);
  },

  methods: {
    onSubmit() {
      this.$validator.validateAll('account').then(async (result) => {
        if (!result) {
          this.$store.commit('setGlobalAlert', {
            msg: "Please make sure you've filled out all of the required fields!",
            error: true
          });
          return;
        }

        try {
          this.errors.clear();

          const dobYear = this.dateOfBirth.year;
          const dobMonth = this.dateOfBirth.month.toString().padStart(2, '0');
          const dobDay = this.dateOfBirth.day.toString().padStart(2, '0');
          const dob = `${dobYear}-${dobMonth}-${dobDay}`;

          const phone = this.phone.replace(/[^\d.]/g, '');
          if (phone.length > 0 && phone.length !== 10) {
            this.$store.commit('setGlobalAlert', {
              msg: 'Please enter your full phone number. For example, (585) 123-1234',
              error: true
            });
            return;
          }

          this.saving = true;
          await this.userClient.updateUserAccount(
            this.firstName,
            this.lastName,
            dob,
            phone,
            this.allowSmsNotifications,
            this.gender,
            this.ethnicity === 'preferNotToAnswer' ? null : this.ethnicity,
            this.currentLocation ? this.currentLocation.id : null,
            this.biography
          );
          await this.studentClient.setCounselorPreferences({
            struggle_area_ids: this.struggleAreas.map((s) => s.id),
            gender_preference: this.genderPreference,
            race_preference: this.ethnicityPreference,
            lgbtq_preference: this.lgbtqPreference || 'false',
            religion_preference: this.religionPreference,
            language_preference: this.languagePreference
          });

          const user = await this.userClient.getRemoteUser();
          this.$store.commit('setUser', user);

          this.$store.commit('setGlobalAlert', {
            msg: 'Profile Updated!'
          });
        } catch (e) {
          this.$store.commit('setGlobalAlert', {
            msg: 'Failed to save account. Please try again!',
            error: true
          });
        } finally {
          this.saving = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.account {
  .profile-button {
    @media (min-width: $md-min) {
      min-width: 150px;
    }
  }

  label {
    @include label;

    font-size: 12px;
  }

  h2 {
    margin-top: 10px;
    font-size: 28px;
  }

  .description {
    margin-bottom: 25px;
    opacity: 70%;
  }

  .recieve-sms-input {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 25px;
    margin-bottom: 15px;
  }
  .replacement-label {
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.1rem;
    color: white;
  }

  .upload-btn {
    width: 100%;
    margin-top: 10px;

    i,
    svg {
      margin-right: 7px;
    }
  }

  .photo-preview {
    .user-avatar {
      @include background-cover;
      @include border-radius(100px);

      background-image: url('~@/assets/img/default-user.jpg');
      height: 40px;
      width: 40px;
      margin-right: 15px;
      display: inline-block;
      vertical-align: top;
      margin-bottom: 15px;
    }

    h2 {
      display: inline-block;
      vertical-align: top;
      margin-bottom: 0;
      line-height: 40px;
      padding-top: 0;
      margin-top: 0;
      white-space: nowrap;
    }
  }

  .right-lg {
    opacity: 0.75;
    position: relative;
    top: -8px;

    label {
      text-transform: none !important;
    }

    @media (min-width: $md-min) {
      text-align: right;
      top: 0;
    }
  }
}
</style>
